import {
  Context,
  ActiveContext,
  Product,
  Notification,
  PersonProfile,
  NavigationItem,
  AdminDashboardData,
  InstitutionProfile,
  Campus,
  InstitutionUser,
  RoleDefinition,
  AdminTask,
  AcademicYear,
  ApprovalRequest,
  InstitutionService,
  AcademicStructureUnitNode,
  EduCoreAcademicYear,
  EduCoreClassGroup,
  IdentifierConfig,
  IdentifierConfigs,
  IdentifierRepairResult,
  IdentifierRenumberResult,
  WorkflowTemplate,
  ImportJob,
  MarketplaceConfig,
  MarketplaceRoleOption,
  MerchantSummary,
  MerchantDetail,
  MerchantProfile,
  MerchantCampusAssignment,
  MerchantWorkflowCase,
  MerchantDocument,
  StartOnboardingRequest,
  ParentAccessPolicy,
  AuditEvent,
  SecurityOverview,
  SupportTicket,
} from '@/types';
import type {
  BrandingAssetUploadTicket,
  ImportBrandingPackageRequest,
  InstitutionBranding,
} from '@/types/platform';
import { getCsrfToken, redirectToLogin } from '@/auth/session';

/**
 * Every call goes through the identity service's BFF proxy.
 *
 * The browser holds no token, so it cannot call Core directly — Core authenticates
 * bearer tokens and a cookie means nothing to it. The proxy accepts the session
 * cookie, attaches an access token scoped to the one upstream being called, and
 * forwards the request. That scoping is deliberate: a token valid at every product
 * API at once would let one compromised upstream replay it against the rest.
 *
 * Upstream names come from BFF_UPSTREAMS in the identity service's .env, where they
 * are an allowlist.
 */
const ACCOUNTS_API =
  import.meta.env.VITE_ACCOUNTS_API_URL || 'http://localhost:4000/api/v1';

const CORE = `${ACCOUNTS_API}/bff/core`;
const IDENTITY = `${ACCOUNTS_API}/bff/identity`;
const ASSIST = `${ACCOUNTS_API}/bff/assist`;

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/**
 * A failed call, carrying the status the caller needs to tell failures apart.
 *
 * A bare Error collapses "this endpoint does not exist yet" into the same thing as
 * "the server is broken", and the two want different words on screen: one is a
 * feature that has not shipped, the other is an incident. Status 0 means the
 * request never reached a server at all.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly path: string;

  constructor(message: string, status: number, path: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
  }

  /** The route is absent — typically an upstream that has not been deployed yet. */
  get isMissingEndpoint(): boolean {
    return this.status === 404;
  }

  /** The caller is authenticated but not allowed. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** The request never completed: offline, DNS, CORS, or the API is down. */
  get isUnreachable(): boolean {
    return this.status === 0;
  }
}

async function request<T>(base: string, path: string, options?: RequestInit): Promise<T> {
  const method = (options?.method ?? 'GET').toUpperCase();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options?.headers as Record<string, string>) ?? {}),
  };

  // Double-submit CSRF. SameSite=Lax blocks cross-site requests, but every
  // CampusOne product is same-site with this API, so a compromised product
  // subdomain could otherwise post with the user's cookie attached.
  const csrf = getCsrfToken();
  if (csrf && !SAFE_METHODS.includes(method)) {
    headers['X-CampusOne-CSRF'] = csrf;
  }

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      ...options,
      method,
      // Without this the browser omits the session cookie on a cross-origin request
      // and every call is anonymous.
      credentials: 'include',
      headers,
    });
  } catch (cause) {
    // fetch only rejects when the request never happened. Reporting that as a
    // generic failure sends people looking for a bug in a service they cannot
    // reach, so it gets its own status.
    throw new ApiError((cause as Error).message || 'Network request failed', 0, path);
  }

  if (!res.ok) {
    if (res.status === 401) {
      redirectToLogin();
      throw new ApiError('Session expired', 401, path);
    }
    const err = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(err?.message || res.statusText || `Request failed: ${res.status}`, res.status, path);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Strips the envelope campusone-core-service wraps every successful response in.
 *
 * Core installs ResponseInterceptor globally in main.ts, so every 2xx body is
 * `{ success: true, data: <the payload>, timestamp, latency, correlationId }`. The
 * envelope is a transport concern, and until now it leaked into roughly forty
 * normalisers that each had to remember it. Most did not.
 *
 * The failure it caused was silent and looked like empty data. Core's paged
 * endpoints return their own `{ data: [...], total, page, limit, totalPages }`, so
 * the wire carried a DOUBLE `data` nesting. `unwrap` descends one level and only
 * accepts an array, so `payload.data` — a paged object — matched nothing and every
 * `rowsWith(unwrap(...))` call site returned `[]`. That is why the institution
 * register was empty while the network tab plainly showed rows: the console asked
 * correctly, the service answered correctly, and the parse dropped everything.
 * Others threw instead: normalizeInstitutionDetail, normalizeInvitation and
 * normalizeResolvedPolicies all read a field off the envelope and reported the
 * response as unreadable.
 *
 * Unwrapping once, here, is what stops the next normaliser from having to know.
 * The tolerant ones are unaffected — they already break out when the payload looks
 * right, and `unwrapObject`'s own envelope check simply finds nothing left to do.
 *
 * Errors are deliberately NOT touched: GlobalExceptionFilter serves those, they
 * carry no envelope, and `request` has already thrown by the time this runs.
 */
function unwrapCoreEnvelope(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;
  // Keyed on `success === true` alongside `data`, not on `data` alone: a record is
  // entitled to a `data` property of its own, and only the interceptor pairs the two.
  if (payload.success !== true || !('data' in payload)) return payload;
  return payload.data;
}

/** Calls CampusOne Core through the BFF. Exported for feature-scoped API modules. */
export const core = <T>(path: string, options?: RequestInit) =>
  request<unknown>(CORE, path, options).then((payload) => unwrapCoreEnvelope(payload) as T);
/**
 * Strips the envelope campusone-identity-service wraps successful responses in.
 *
 * A DIFFERENT SHAPE FROM CORE'S, which is why it needs its own function. Identity's
 * TransformInterceptor returns `{ data: T }` with no `success` field, so
 * `unwrapCoreEnvelope` — which keys on `success === true` — correctly leaves it alone
 * and the console was handed the envelope. `normalizeInvitation` then looked for its
 * required fields on that wrapper, found none, and threw "the server returned an
 * invitation this app cannot read" about a perfectly good invitation.
 *
 * The condition mirrors the interceptor exactly rather than approximating it. That
 * interceptor passes a value through UNCHANGED when it already has both `data` and
 * `total` — its way of not re-wrapping a paged result — and wraps everything else. So
 * unwrapping only when the keys are a subset of {data, meta} leaves paged responses
 * intact, which matters because `unwrap` and `normalizePage` read `total` off the root
 * and would find nothing if this peeled it away.
 *
 * The narrow key check is also what stops a record with a legitimate `data` property of
 * its own from being mistaken for an envelope.
 */
function unwrapIdentityEnvelope(payload: unknown): unknown {
  if (!isRecord(payload)) return payload;
  const keys = Object.keys(payload);
  if (!keys.includes('data')) return payload;
  if (keys.some((key) => key !== 'data' && key !== 'meta')) return payload;
  return payload.data;
}

/** Calls the identity service through the BFF. Exported for feature-scoped API modules. */
export const identity = <T>(path: string, options?: RequestInit) =>
  request<unknown>(IDENTITY, path, options).then((payload) => unwrapIdentityEnvelope(payload) as T);

/**
 * Calls CampusOne Assist through the BFF. Exported for feature-scoped API modules.
 *
 * NO ENVELOPE TO STRIP, unlike core and identity. The assist API installs no
 * transform interceptor, so a 2xx body is already the payload — running either
 * unwrapper over it would silently return `undefined` for any response that
 * happened to carry a `data` key of its own.
 *
 * It goes through the BFF for the same reason everything else does: this browser
 * holds no token, and assist authenticates a bearer token and verifies the
 * institution from headers only the proxy can sign. A direct call from here
 * arrives with neither and is refused.
 */
export const assist = <T>(path: string, options?: RequestInit) =>
  request<T>(ASSIST, path, options);

/**
 * Fetches a stored branding asset and returns a blob URL to render it from.
 *
 * The asset route is authenticated (any signed-in user may read the institution's
 * logo), so an <img> tag cannot load it directly — it would send no cookie. The
 * bytes are therefore fetched like any other API call and handed back as an
 * object URL. Callers revoke the URL when they are done with it.
 */
export async function brandingAssetBlobUrl(institutionId: string, kind: string): Promise<string> {
  const res = await fetch(`${CORE}/branding/${institutionId}/assets/${kind}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new ApiError('Failed to load branding asset', res.status, `/branding/assets/${kind}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function normalizeProductList(payload: unknown): Product[] {
  const candidates = [
    payload,
    isRecord(payload) ? payload.products : undefined,
    isRecord(payload) ? payload.data : undefined,
    isRecord(payload) ? payload.items : undefined,
  ];
  const list = candidates.find(Array.isArray);
  if (!Array.isArray(list)) return [];

  return list.filter(isProduct);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isProduct(value: unknown): value is Product {
  if (!isRecord(value)) return false;
  return (
    typeof value.code === 'string' &&
    typeof value.name === 'string' &&
    typeof value.description === 'string' &&
    typeof value.status === 'string'
  );
}

function normalizeContextList(payload: unknown): Context[] {
  const candidates = [
    payload,
    isRecord(payload) ? payload.contexts : undefined,
    isRecord(payload) ? payload.data : undefined,
    isRecord(payload) ? payload.items : undefined,
  ];
  const list = candidates.find(Array.isArray);
  return Array.isArray(list) ? list.filter(isContext) : [];
}

function normalizeActiveContext(payload: unknown): ActiveContext | null {
  const candidates = [
    payload,
    isRecord(payload) ? payload.context : undefined,
    isRecord(payload) ? payload.activeContext : undefined,
    isRecord(payload) ? payload.data : undefined,
  ];

  // Already nested — accept as-is.
  const nested = candidates.find(isActiveContext);
  if (nested) return nested;

  // Otherwise the flat server shape, which is what every service actually returns.
  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    const mapped = activeContextFromFlat(candidate);
    if (mapped) return mapped;
  }
  return null;
}

function isContext(value: unknown): value is Context {
  if (!isRecord(value)) return false;
  const hasInstitution = typeof value.institutionId === 'string';
  const hasGroup = typeof value.groupId === 'string';
  return (
    typeof value.id === 'string' &&
    typeof value.roleAssignmentId === 'string' &&
    typeof value.roleCode === 'string' &&
    typeof value.roleName === 'string' &&
    // A context is scoped to an institution OR to a group, never both and never
    // neither. Group contexts arrive with institutionId ABSENT or null (identity
    // omits the key entirely for a group seat — `?? undefined` on the wire, see
    // authorization.service getAvailableContexts), so this is the one place the
    // two shapes get reconciled for the list.
    (hasInstitution || hasGroup) &&
    (value.institutionId == null || hasInstitution) &&
    Array.isArray(value.products)
  );
}

/**
 * Builds the nested ActiveContext this app types from the flat one the server sends.
 *
 * THE TWO SHAPES DISAGREE, AND THEY DISAGREE INSIDE THIS FILE TOO. `Context` (the list) is
 * flat — roleName, institutionName — while `ActiveContext` nests them under `role` and
 * `institution`. The server sends one shape for both. isActiveContext therefore rejected
 * every response, normalizeActiveContext returned null, and the header read "No contexts
 * available" immediately after a switch that had returned 201 with a complete, correct
 * context. Nothing logged: a guard that returns false is indistinguishable from an empty
 * answer.
 *
 * Mapping here rather than changing the server keeps one client's view model out of a
 * contract three services already read, and leaves every component consuming
 * `activeContext.role.name` untouched.
 */
function activeContextFromFlat(value: Record<string, unknown>): ActiveContext | null {
  const id = value.id ?? value.contextId;
  const roleCode = value.roleCode;
  const institutionId = value.institutionId;
  const groupId = value.groupId;
  const hasGroup = typeof groupId === 'string' && (institutionId === null || institutionId === undefined);
  if (typeof id !== 'string' || typeof roleCode !== 'string') return null;
  if (typeof institutionId !== 'string' && !hasGroup) return null;

  return {
    id,
    role: {
      code: roleCode,
      // Falls back to the code: PORTAL_ADMINISTRATOR still tells the operator where they
      // are, where an empty string leaves the banner blank.
      name: typeof value.roleName === 'string' ? value.roleName : roleCode,
      category: typeof value.roleCategory === 'string' ? value.roleCategory : '',
    },
    // A group context has no institution — identity reports institutionId as
    // null on it, and the seat renders from `group` instead.
    institution:
      typeof institutionId === 'string'
        ? {
            id: institutionId,
            name: typeof value.institutionName === 'string' ? value.institutionName : '',
            code: typeof value.institutionCode === 'string' ? value.institutionCode : '',
          }
        : null,
    ...(hasGroup
      ? {
          group: {
            id: groupId,
            name: typeof value.groupName === 'string' ? value.groupName : groupId,
            ...(typeof value.groupType === 'string' ? { type: value.groupType } : {}),
          },
        }
      : {}),
    ...(typeof value.campusId === 'string'
      ? { campus: { id: value.campusId, name: '' } }
      : {}),
    // Carried through so navigation can gate on what the seat may do. Left
    // absent rather than defaulted to [] when the service does not send it:
    // an empty list means "holds nothing", and guessing that would hide tools
    // from people who do hold them.
    ...(Array.isArray(value.permissions)
      ? { permissions: value.permissions.filter((entry): entry is string => typeof entry === 'string') }
      : {}),
  };
}

function isActiveContext(value: unknown): value is ActiveContext {
  if (!isRecord(value) || !isRecord(value.role)) return false;
  const institution = isRecord(value.institution) ? value.institution : null;
  const group = isRecord(value.group) ? value.group : null;
  // A seat is at an institution OR at a group, never both and never neither.
  if (!institution && !group) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.role.code === 'string' &&
    typeof value.role.name === 'string' &&
    (institution
      ? typeof institution.id === 'string' &&
        typeof institution.name === 'string' &&
        typeof institution.code === 'string'
      : true) &&
    (group ? typeof group.id === 'string' && typeof group.name === 'string' : true)
  );
}

export const api = {
  // Session state is not proxied: /auth/session is the BFF's own endpoint and needs
  // no token. See @/auth/session.
  me: {
    // The person record lives in the identity service, not Core.
    profile: () => identity<PersonProfile>('/me'),
  },
  contexts: {
    // Core mounts these under /context (singular), and switching is a POST to
    // /context/switch/:id — not /contexts/:id/activate.
    list: () => core<unknown>('/context/available').then(normalizeContextList),
    getActive: () => core<unknown>('/context/active').then(normalizeActiveContext),
    setActive: (id: string) =>
      core<unknown>(`/context/switch/${id}`, { method: 'POST' }).then((payload) => {
        const context = normalizeActiveContext(payload);
        if (!context) throw new Error('Invalid active context response');
        return context;
      }),
  },
  navigation: {
    list: () => core<NavigationItem[]>('/navigation'),
  },
  products: {
    // BFF deployments may return a bare array or an envelope while they are
    // being rolled out. Normalize both forms before they reach React state.
    list: () => core<unknown>('/products').then(normalizeProductList),
    get: (code: string) => core<Product>(`/products/${code}`),
    /**
     * The institution's products, readable by ANY member of it — this is the
     * portal route. The admin.products.list twin sits behind the administrator
     * guard, and pointing the portal at it rendered every non-administrator's
     * 403 as "No products available".
     */
    forInstitution: (institutionId: string) =>
      core<Product[]>(`/institutions/${institutionId}/products`),
  },
  dashboard: {
    get: (projectionType: string) =>
      core<Record<string, unknown>>(`/dashboard/${projectionType}`),
  },
  announcements: {
    list: () => core<unknown[]>('/announcements'),
  },

  onboarding: {
    /**
     * Creates an institution and its first administrator.
     *
     * Goes to the IDENTITY service, not Core: institutions, persons and role
     * assignments are owned there. Core projects them and owns none of it.
     */
    institution: (body: Record<string, unknown>) =>
      identity<{
        institution: { id: string; code: string; name: string; status: string };
        administrator: {
          personId: string;
          email: string;
          roleCode: string;
          signInEnabled: boolean;
        };
        warnings: string[];
      }>('/onboarding/institutions', { method: 'POST', body: JSON.stringify(body) }),
  },

  /**
   * NOT IMPLEMENTED SERVER-SIDE. Core has no notifications controller at all.
   *
   * Stubbed rather than pointed at a route that returns 404, which would surface as
   * a generic request failure and read like a proxy or auth fault instead of a
   * missing feature. The empty list lets the bell and the page render their
   * "nothing here" state, which is accurate — there is no notification store yet.
   *
   * Replace with `core(...)` calls once the endpoints exist.
   */
  notifications: {
    // Real endpoints now. These were stubs that logged warnUnimplemented and resolved to
    // an empty array, so the page has always shown nothing.
    //
    // No recipient is sent on any of them, deliberately: the server takes it from the
    // verified token. A client that could name a recipient is a client that could read
    // somebody else's inbox.
    list: (options: { unread?: boolean; limit?: number } = {}) => {
      const query = new URLSearchParams();
      if (options.unread) query.set('unread', 'true');
      if (options.limit) query.set('limit', String(options.limit));
      const qs = query.toString();
      return core<Notification[]>(`/me/notifications${qs ? `?${qs}` : ''}`);
    },
    unreadCount: () => core<{ count: number }>('/me/notifications/unread-count'),
    markRead: (id: string) => core<Notification>(`/me/notifications/${id}/read`, { method: 'POST' }),
    markAllRead: () => core<{ updated: number }>('/me/notifications/read-all', { method: 'POST' }),
  },

  /**
   * Institution Administrator API — scoped to the active institution context.
   * All routes are prefixed with /admin/institution/ on CampusOne Core.
   */
  admin: {
    dashboard: {
      get: (institutionId: string) =>
        core<AdminDashboardData>(`/admin/institution/${institutionId}/dashboard`),
    },
    profile: {
      get: (institutionId: string) =>
        core<InstitutionProfile>(`/admin/institution/${institutionId}/profile`).catch(() => {
          warnUnimplemented('admin-profile');
          return null as unknown as InstitutionProfile;
        }),
      update: (institutionId: string, body: Partial<InstitutionProfile>) =>
        core<InstitutionProfile>(`/admin/institution/${institutionId}/profile`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      submitRestrictedChange: (institutionId: string, body: Record<string, unknown>) =>
        core<{ requestId: string }>(`/admin/institution/${institutionId}/profile/change-request`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },
    campuses: {
      list: (institutionId: string) =>
        core<Campus[]>(`/admin/institution/${institutionId}/campuses`).catch(() => {
          warnUnimplemented('admin-campuses');
          return [];
        }),
      create: (institutionId: string, body: Partial<Campus>) =>
        core<Campus>(`/admin/institution/${institutionId}/campuses`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (institutionId: string, campusId: string, body: Partial<Campus>) =>
        core<Campus>(`/admin/institution/${institutionId}/campuses/${campusId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      suspend: (institutionId: string, campusId: string) =>
        core<void>(`/admin/institution/${institutionId}/campuses/${campusId}/suspend`, {
          method: 'POST',
        }),
      activate: (institutionId: string, campusId: string) =>
        core<void>(`/admin/institution/${institutionId}/campuses/${campusId}/activate`, {
          method: 'POST',
        }),
      close: (institutionId: string, campusId: string) =>
        core<void>(`/admin/institution/${institutionId}/campuses/${campusId}/close`, {
          method: 'POST',
        }),
    },
    users: {
      list: (institutionId: string) =>
        core<InstitutionUser[]>(`/admin/institution/${institutionId}/users`).catch(() => {
          warnUnimplemented('admin-users');
          return [];
        }),
      invite: (
        institutionId: string,
        body: {
          email: string;
          roleCode: string;
          campusId?: string;
          firstName?: string;
          lastName?: string;
          title?: string;
          phone?: string;
          referenceNumber?: string;
          department?: string;
        },
      ) =>
        core<{
          id: string;
          /** What actually happened behind the invite: issued/delivered, and
           *  the hand-over link when mail could not be sent. */
          invitation?: {
            issued: boolean;
            delivered: boolean;
            inviteUrl?: string;
            warnings: string[];
          };
        }>(`/admin/institution/${institutionId}/users/invite`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (
        institutionId: string,
        userId: string,
        body: Partial<{
          name: string;
          title: string;
          phone: string;
          referenceNumber: string;
          department: string;
          campusId: string;
          roleCode: string;
          roleStartDate: string;
          roleEndDate: string;
        }>,
      ) =>
        core<InstitutionUser>(`/admin/institution/${institutionId}/users/${userId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      remove: (institutionId: string, userId: string) =>
        core<{ removed: boolean }>(`/admin/institution/${institutionId}/users/${userId}`, {
          method: 'DELETE',
        }),
      resendInvitation: (institutionId: string, userId: string) =>
        core<
          InstitutionUser & {
            invitation?: { issued: boolean; delivered: boolean; inviteUrl?: string; warnings: string[] };
          }
        >(`/admin/institution/${institutionId}/users/${userId}/resend`, { method: 'POST' }),
      revokeInvitation: (institutionId: string, userId: string) =>
        core<void>(`/admin/institution/${institutionId}/users/${userId}/revoke`, { method: 'POST' }),
      suspend: (institutionId: string, userId: string) =>
        core<void>(`/admin/institution/${institutionId}/users/${userId}/suspend`, { method: 'POST' }),
      reactivate: (institutionId: string, userId: string) =>
        core<void>(`/admin/institution/${institutionId}/users/${userId}/reactivate`, { method: 'POST' }),
    },
    roles: {
      /**
       * With an institutionId, core narrows the catalogue to the roles that
       * institution's TYPE can staff — a secondary school stops being offered
       * Professor and Dean. Without one, the full platform catalogue.
       */
      listDefinitions: (institutionId?: string) =>
        core<RoleDefinition[]>(
          institutionId
            ? `/admin/institution/${institutionId}/roles`
            : '/admin/institution/roles',
        ).catch(() => {
          warnUnimplemented('admin-roles');
          return [];
        }),
      assign: (institutionId: string, body: { identityId: string; roleCode: string; campusId?: string; startDate?: string; endDate?: string }) =>
        core<{ id: string }>(`/admin/institution/${institutionId}/roles/assign`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },
    products: {
      list: (institutionId: string) =>
        core<Product[]>(`/admin/institution/${institutionId}/products`).catch(() => {
          warnUnimplemented('admin-products');
          return [];
        }),
    },
    branding: {
      get: (institutionId: string) =>
        core<Record<string, unknown>>(`/admin/institution/${institutionId}/branding`).catch(() => {
          warnUnimplemented('admin-branding');
          return null;
        }),
      update: (institutionId: string, body: Record<string, unknown>) =>
        core<void>(`/admin/institution/${institutionId}/branding`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      /**
       * Uploads a branding image THROUGH core, which stores it and answers with
       * the object key.
       *
       * The key is generated by the service and is the ONLY key the branding
       * update will accept for this institution — see the `logo` record step in
       * the branding page.
       *
       * BASE64, NOT MULTIPART, and not by preference: every call here goes
       * through the accounts BFF, which re-serialises request bodies as JSON. A
       * multipart file part arrives at core as `{}` — the file gone, behind a
       * success. Base64 inside JSON is the only shape that survives that hop.
       *
       * This replaced a presigned direct-to-storage PUT. Object storage is now
       * private to core's stack, so a presigned URL points at an address no
       * browser can reach; reads were already streamed through core, so the
       * upload was the only thing keeping storage publicly exposed.
       */
      uploadAsset: (
        institutionId: string,
        body: { kind: string; format: string; dataBase64: string },
      ) =>
        core<BrandingAssetUploadTicket>(`/admin/institution/${institutionId}/branding/assets`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      /**
       * Imports a branding package: design tokens, stylesheet and description in
       * one JSON body. The browser reads the folder's files as text; core parses
       * the tokens, grades the extracted palette and stores the stylesheet as a
       * branding asset.
       */
      importPackage: (institutionId: string, body: ImportBrandingPackageRequest) =>
        core<InstitutionBranding>(`/admin/institution/${institutionId}/branding/package`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },
    reports: {
      list: () =>
        core<{ id: string; title: string; category: string; source: string }[]>('/admin/institution/reports').catch(() => {
          warnUnimplemented('admin-reports');
          return [];
        }),
      get: (reportId: string) =>
        core<unknown>(`/admin/institution/reports/${reportId}`),
    },
    tasks: {
      list: () =>
        core<AdminTask[]>('/admin/institution/tasks').catch(() => {
          warnUnimplemented('admin-tasks');
          return [];
        }),
      complete: (taskId: string) =>
        core<void>(`/admin/institution/tasks/${taskId}/complete`, { method: 'POST' }),
    },
    academicYears: {
      list: (institutionId: string) =>
        core<AcademicYear[]>(`/admin/institution/${institutionId}/academic-years`).catch(() => {
          warnUnimplemented('admin-academic-years');
          return [];
        }),
      create: (institutionId: string, body: { label: string; startDate: string; endDate: string }) =>
        core<AcademicYear>(`/admin/institution/${institutionId}/academic-years`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (institutionId: string, id: string, body: { label: string; startDate: string; endDate: string }) =>
        core<AcademicYear>(`/admin/institution/${institutionId}/academic-years/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      delete: (institutionId: string, id: string) =>
        core<void>(`/admin/institution/${institutionId}/academic-years/${id}`, {
          method: 'DELETE',
        }),
      submitForApproval: (institutionId: string, id: string) =>
        core<void>(`/admin/institution/${institutionId}/academic-years/${id}/submit`, {
          method: 'POST',
        }),
    },
    approvals: {
      list: (institutionId: string, status?: string) =>
        core<ApprovalRequest[]>(
          `/admin/institution/${institutionId}/approvals${status ? `?status=${status}` : ''}`,
        ),
      approve: (institutionId: string, requestId: string, note?: string) =>
        core<ApprovalRequest>(`/admin/institution/${institutionId}/approvals/${requestId}/approve`, {
          method: 'POST',
          body: JSON.stringify(note ? { note } : {}),
        }),
      reject: (institutionId: string, requestId: string, note?: string) =>
        core<ApprovalRequest>(`/admin/institution/${institutionId}/approvals/${requestId}/reject`, {
          method: 'POST',
          body: JSON.stringify(note ? { note } : {}),
        }),
    },
    workflows: {
      list: (institutionId: string) =>
        core<WorkflowTemplate[]>(`/admin/institution/${institutionId}/workflows`),
      configure: (
        institutionId: string,
        code: string,
        body: { participantsCount?: number; approvalLevels?: number; active?: boolean },
      ) =>
        core<WorkflowTemplate>(`/admin/institution/${institutionId}/workflows/${code}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
    },
    services: {
      list: (institutionId: string) =>
        core<InstitutionService[]>(`/admin/institution/${institutionId}/services`),
      configure: (
        institutionId: string,
        code: string,
        body: { configured?: boolean; academicApplicability?: string; campusAssignment?: string },
      ) =>
        core<InstitutionService>(`/admin/institution/${institutionId}/services/${code}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
    },
    academicStructure: {
      listUnits: (institutionId: string) =>
        core<AcademicStructureUnitNode[]>(
          `/admin/institution/${institutionId}/academic-structure/units`,
        ),
      updateUnit: (
        institutionId: string,
        unitId: string,
        body: { name?: string; code?: string; ordinal?: number; isActive?: boolean },
      ) =>
        core<Record<string, unknown>>(
          `/admin/institution/${institutionId}/academic-structure/units/${unitId}`,
          { method: 'PUT', body: JSON.stringify(body) },
        ),
      listYears: (institutionId: string) =>
        core<EduCoreAcademicYear[]>(
          `/admin/institution/${institutionId}/academic-structure/years`,
        ),
      getActiveYear: (institutionId: string) =>
        core<EduCoreAcademicYear | null>(
          `/admin/institution/${institutionId}/academic-structure/years/active`,
        ),
      listClassGroups: (institutionId: string, academicYearId: string) =>
        core<EduCoreClassGroup[]>(
          `/admin/institution/${institutionId}/academic-structure/class-groups?academicYearId=${encodeURIComponent(academicYearId)}`,
        ),
      createClassGroup: (
        institutionId: string,
        body: {
          academicYearId: string;
          unitId: string;
          code: string;
          name: string;
          studyMode?: 'DAY' | 'EVENING' | null;
          capacity?: number | null;
        },
      ) =>
        core<EduCoreClassGroup>(
          `/admin/institution/${institutionId}/academic-structure/class-groups`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      bulkCreateClassGroups: (
        institutionId: string,
        body: {
          academicYearId: string;
          unitId: string;
          items: {
            code: string;
            name: string;
            studyMode?: 'DAY' | 'EVENING' | null;
            capacity?: number | null;
          }[];
        },
      ) =>
        core<EduCoreClassGroup[]>(
          `/admin/institution/${institutionId}/academic-structure/class-groups/bulk`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      updateClassGroup: (
        institutionId: string,
        classGroupId: string,
        body: {
          code?: string;
          name?: string;
          studyMode?: 'DAY' | 'EVENING' | null;
          capacity?: number | null;
          isActive?: boolean;
        },
      ) =>
        core<EduCoreClassGroup>(
          `/admin/institution/${institutionId}/academic-structure/class-groups/${classGroupId}`,
          { method: 'PUT', body: JSON.stringify(body) },
        ),
      getNumberingConfig: (institutionId: string) =>
        core<IdentifierConfigs>(
          `/admin/institution/${institutionId}/academic-structure/numbering`,
        ),
      updateNumberingConfig: (
        institutionId: string,
        body: {
          identifierType?: 'ADMISSION' | 'MATRICULATION';
          institutionCode?: string;
          format?: string;
        },
      ) =>
        core<IdentifierConfig>(
          `/admin/institution/${institutionId}/academic-structure/numbering`,
          { method: 'PUT', body: JSON.stringify(body) },
        ),
      repairIdentifiers: (institutionId: string) =>
        core<IdentifierRepairResult>(
          `/admin/institution/${institutionId}/academic-structure/numbering/repair`,
          { method: 'POST' },
        ),
      renumberIdentifiers: (institutionId: string) =>
        core<IdentifierRenumberResult>(
          `/admin/institution/${institutionId}/academic-structure/numbering/renumber`,
          { method: 'POST' },
        ),
    },
    integrations: {
      list: (institutionId: string) =>
        core<{ service: string; status: string; lastSync?: string }[]>(
          `/admin/institution/${institutionId}/integrations`,
        ),
      check: (institutionId: string, service: string) =>
        core<{ service: string; status: string; lastSync?: string }>(
          `/admin/institution/${institutionId}/integrations/${encodeURIComponent(service)}/check`,
          { method: 'POST' },
        ),
    },
    dataImport: {
      listJobs: (institutionId: string) =>
        core<ImportJob[]>(`/admin/institution/${institutionId}/imports/history`),
      upload: (institutionId: string, category: string, fileName: string, fileData: string) =>
        core<{ id: string; status: string }>(
          `/admin/institution/${institutionId}/imports/${category}`,
          { method: 'POST', body: JSON.stringify({ fileName, fileData }) },
        ),
      template: (institutionId: string, category: string) =>
        core<{ fileName: string; content: string }>(
          `/admin/institution/${institutionId}/imports/${category}/template`,
        ),
    },
    marketplace: {
      // No mock fallback. The endpoint exists now, and returning a fabricated config on
      // failure is what made a screen of toggles look configured while controlling nothing —
      // a 404 reached the page as plausible settings rather than as an error.
      get: (institutionId: string) =>
        core<MarketplaceConfig>(`/admin/institution/${institutionId}/marketplace`),
      update: (institutionId: string, body: Partial<MarketplaceConfig>) => {
        // Core validates with forbidNonWhitelisted, and GET returns the stored
        // entity (institutionId, timestamps...). A caller that round-trips the
        // fetched object would 400, so only the DTO's fields are sent.
        const payload: Partial<MarketplaceConfig> = {};
        if (body.enabled !== undefined) payload.enabled = body.enabled;
        if (body.allowedCategories !== undefined) payload.allowedCategories = body.allowedCategories;
        if (body.moderators !== undefined) payload.moderators = body.moderators;
        if (body.enabledCampuses !== undefined) payload.enabledCampuses = body.enabledCampuses;
        if (body.policies !== undefined) payload.policies = body.policies;
        return core<void>(`/admin/institution/${institutionId}/marketplace`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      },
      createMerchant: (institutionId: string, body: StartOnboardingRequest) =>
        core<{ merchantId: string; workflowCaseId: string }>(`/admin/institution/${institutionId}/marketplace/merchants`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      getMerchants: (institutionId: string) =>
        core<MerchantSummary[]>(`/admin/institution/${institutionId}/marketplace/merchants`),
      getMerchantDetail: async (institutionId: string, id: string) => {
        const data = await core<{
          merchant: MerchantSummary;
          profile: MerchantProfile | null;
          campusAssignments: MerchantCampusAssignment[];
          workflowCase: MerchantWorkflowCase | null;
          documents: MerchantDocument[];
        }>(`/admin/institution/${institutionId}/marketplace/merchants/${id}`);
        return {
          ...data.merchant,
          profile: data.profile,
          campusAssignments: data.campusAssignments,
          workflowCase: data.workflowCase,
          documents: data.documents,
        } as MerchantDetail;
      },
      approveMerchant: (institutionId: string, id: string, ownerEmail: string) =>
        core<void>(`/admin/institution/${institutionId}/marketplace/merchants/${id}/approve`, {
          method: 'POST', body: JSON.stringify({ ownerEmail }),
        }),
      rejectMerchant: (institutionId: string, id: string, reason: string) =>
        core<void>(`/admin/institution/${institutionId}/marketplace/merchants/${id}/reject`, {
          method: 'POST', body: JSON.stringify({ reason }),
        }),
      submitMerchant: (institutionId: string, id: string) =>
        core<void>(`/admin/institution/${institutionId}/marketplace/merchants/${id}/submit`, { method: 'POST' }),
      updateMerchantProfile: (institutionId: string, id: string, body: Record<string, unknown>) =>
        core<void>(`/admin/institution/${institutionId}/marketplace/merchants/${id}/profile`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),
      listRoles: (institutionId: string) =>
        core<MarketplaceRoleOption[]>(`/admin/institution/${institutionId}/marketplace/roles`),
      inviteRole: (institutionId: string, body: { email: string; roleCode: string; merchantId?: string; merchantName?: string }) =>
        core<{ invitationId: string; roleCode: string; email: string; delivered: boolean; inviteUrl?: string; warnings: string[] }>(
          `/admin/institution/${institutionId}/marketplace/roles/invite`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
    },
    parentAccess: {
      get: (institutionId: string) =>
        core<ParentAccessPolicy>(`/admin/institution/${institutionId}/parent-access`),
      update: (institutionId: string, body: Partial<ParentAccessPolicy>) =>
        core<void>(`/admin/institution/${institutionId}/parent-access`, { method: 'PUT', body: JSON.stringify(body) }),
    },
    audit: {
      list: (institutionId: string, params?: { category?: string; actor?: string; from?: string; to?: string }) => {
        const query = new URLSearchParams();
        if (params?.category) query.set('category', params.category);
        if (params?.actor) query.set('actor', params.actor);
        if (params?.from) query.set('from', params.from);
        if (params?.to) query.set('to', params.to);
        const qs = query.toString();
        return core<AuditEvent[]>(`/admin/institution/${institutionId}/audit${qs ? `?${qs}` : ''}`);
      },
    },
    security: {
      overview: (institutionId: string) =>
        core<SecurityOverview>(`/admin/institution/${institutionId}/security`),
    },
    support: {
      listTickets: (institutionId: string) =>
        core<SupportTicket[]>(`/admin/institution/${institutionId}/support`),
      createTicket: (institutionId: string, body: { subject: string; description: string; category: string; productCode?: string }) =>
        core<{ id: string }>(`/admin/institution/${institutionId}/support`, { method: 'POST', body: JSON.stringify(body) }),
    },
  },
};

const warned = new Set<string>();

/** Once per feature per page load; a warning on every render is just noise. */
function warnUnimplemented(feature: string): void {
  if (warned.has(feature)) return;
  warned.add(feature);
  console.warn(
    `[campusone] "${feature}" has no server implementation yet — CampusOne Core ` +
      'exposes no such endpoint. The UI renders its empty state.',
  );
}
